import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DefaultButton, MessageBar, MessageBarType, Spinner } from '@fluentui/react';
import { useSearchParams } from 'react-router-dom';
import ProspectingAuthContext from '../auth/ProspectingAuthContext';
import { getCommuneMapContext, getMapGeometry, getPotentialMap, refreshRspoMapLocations } from '../api/prospectingApi';
import SegmentedControl from '../components/SegmentedControl';
import MarketBreadcrumbs from '../components/MarketBreadcrumbs';
import MarketSearch from '../components/MarketSearch';
import MarketTypeFilter, { KIDPLACE_SEGMENTS } from '../components/MarketTypeFilter';
import MarketMap from '../components/MarketMap';
import MarketSideList from '../components/MarketSideList';
import CompetitionLegend from '../components/CompetitionLegend';
import ActivityLegend from '../components/ActivityLegend';
import EntityPanel from '../components/EntityPanel';
import CrmSyncDialog from '../components/CrmSyncDialog';
import CompetitionImportDialog from '../components/CompetitionImportDialog';
import { sortByMetric } from '../utils/metrics';
import { sortByCompetition } from '../utils/competition';
import { sortByActivity } from '../utils/activity';
import { applyKidplaceClientFilter } from '../utils/clientMarket';

const PRODUCTS = [
  { value: 'kidplace', label: 'Kidplace' },
  { value: 'grants', label: 'Dotacje' },
  { value: 'erecruitment', label: 'eRekrutacje' },
];

const METRICS = [
  { value: 'arr', label: 'ARR' },
  { value: 'mrr', label: 'MRR' },
  { value: 'children', label: 'Dzieci' },
  { value: 'score', label: 'Scoring' },
  { value: 'effective_score', label: 'Efektywny scoring' },
];

function defaultMetric(product, scope) {
  if (product === 'grants' || product === 'erecruitment') return 'arr';
  return scope === 'commune' ? 'score' : 'arr';
}

function geometryRequest(scope, scopeId) {
  if (scope === 'country') return { level: 'voivodeship' };
  if (scope === 'voivodeship') return { level: 'county', parent: scopeId };
  if (scope === 'county') return { level: 'commune', parent: scopeId };
  return { level: 'commune', code: scopeId };
}

function childScope(scope) {
  return { country: 'voivodeship', voivodeship: 'county', county: 'commune' }[scope] || null;
}

export default function MarketMapPage() {
  const authValue = useContext(ProspectingAuthContext);
  const user = Array.isArray(authValue) ? authValue[0] : authValue?.user || authValue;
  const token = user?.token || '';
  const [params, setParams] = useSearchParams();

  const product = params.get('product') || 'kidplace';
  const scope = params.get('scope') || 'country';
  const scopeId = params.get('scopeId') || '';
  const onlyNewOpenings = params.get('openings') === '1';
  const requestedMetric = params.get('metric') || defaultMetric(product, scope);
  const requestedLayer = params.get('layer') || requestedMetric;
  const allKidplaceSegments = KIDPLACE_SEGMENTS.map((item) => item.value);
  const requestedSegments = String(params.get('segments') || '').replace(/(^|,)nursery(?=,|$)/g, '$1nursery_public,nursery_private')
    .replace(/(^|,)nursery_unverified(?=,|$)/g, '$1nursery_public')
    .replace(/(^|,)nursery_municipal(?=,|$)/g, '$1nursery_public')
    .replace(/(^|,)maluch_plus(?=,|$)/g, '$1maluch_public,maluch_private')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => allKidplaceSegments.includes(value));
  const selectedSegments = requestedSegments.length ? requestedSegments : allKidplaceSegments;

  const [data, setData] = useState(null);
  const [geometry, setGeometry] = useState(null);
  const [mapContext, setMapContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [entityId, setEntityId] = useState(null);
  const [crmTarget, setCrmTarget] = useState(null);
  const [competitionImportOpen, setCompetitionImportOpen] = useState(false);
  const [crmRefresh, setCrmRefresh] = useState(0);
  const [locationRefresh, setLocationRefresh] = useState(0);
  const requestSequence = useRef(0);
  // v31.8: each hierarchy level is loaded once and reused while navigating back/forth.
  // Filter toggles never touch these caches because filtering happens in-memory below.
  const dataCache = useRef(new Map());
  const geometryCache = useRef(new Map());
  const contextCache = useRef(new Map());
  const rspoRefreshedScopes = useRef(new Set());

  const fallbackMetrics = product === 'grants' || product === 'erecruitment'
    ? ['arr']
    : (scope === 'commune'
      ? ['arr', 'mrr', 'children', 'score', 'effective_score']
      : scope === 'county'
        ? ['arr', 'mrr', 'children', 'score', 'effective_score']
        : ['arr']);
  const allowedMetrics = data?.allowed_metrics || fallbackMetrics;
  const metric = allowedMetrics.includes(requestedMetric) ? requestedMetric : allowedMetrics[0];
  const competitionOverlay = product === 'kidplace' && requestedLayer === 'competition';
  const activityOverlay = product === 'kidplace' && requestedLayer === 'activity';
  const layer = competitionOverlay
    ? 'competition'
    : activityOverlay
      ? 'activity'
      : (allowedMetrics.includes(requestedLayer) ? requestedLayer : metric);

  useEffect(() => {
    const next = new URLSearchParams(params);
    let changed = false;
    if (next.has('mode')) {
      next.delete('mode');
      changed = true;
    }
    if (metric !== requestedMetric) {
      next.set('metric', metric);
      changed = true;
    }
    if (layer !== requestedLayer) {
      next.set('layer', layer);
      changed = true;
    }
    if (changed) setParams(next, { replace: true });
  }, [metric, requestedMetric, layer, requestedLayer, params, setParams]);

  useEffect(() => {
    setHoveredId(null);
    setEntityId(null);
  }, [product, scope, scopeId]);

  useEffect(() => {
    if (scope !== 'country' && competitionImportOpen) setCompetitionImportOpen(false);
  }, [scope, competitionImportOpen]);

  useEffect(() => {
    // Never reuse a previous user's cached market read model after auth changes.
    dataCache.current.clear();
    geometryCache.current.clear();
    contextCache.current.clear();
    rspoRefreshedScopes.current.clear();
  }, [token]);

  useEffect(() => {
    const sequence = ++requestSequence.current;
    if (!token) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const scopeKey = `${product}:${scope}:${scopeId || 'PL'}`;
    const geometryArgs = geometryRequest(scope, scopeId);
    const geometryKey = `${geometryArgs.level}:${geometryArgs.parent || ''}:${geometryArgs.code || ''}`;
    const contextKey = scope === 'commune' && scopeId ? String(scopeId) : '';

    const cachedData = dataCache.current.get(scopeKey);
    const cachedGeometry = geometryCache.current.get(geometryKey);
    const cachedContext = contextKey ? contextCache.current.get(contextKey) : null;
    const fullyCached = Boolean(cachedData && cachedGeometry);

    if (cachedData) setData(cachedData);
    else setData(null);
    if (cachedGeometry) setGeometry(cachedGeometry);
    else setGeometry(null);
    if (scope !== 'commune') setMapContext(null);
    else if (cachedContext) setMapContext(cachedContext);
    else setMapContext(null);

    setLoading(!fullyCached);
    setError(null);

    const dataPromise = cachedData
      ? Promise.resolve(cachedData)
      : getPotentialMap(token, { product, scope, scopeId }, controller.signal).then((value) => {
        dataCache.current.set(scopeKey, value);
        return value;
      });
    const geometryPromise = cachedGeometry
      ? Promise.resolve(cachedGeometry)
      : getMapGeometry(token, geometryArgs, controller.signal).then((value) => {
        geometryCache.current.set(geometryKey, value);
        return value;
      });
    const contextPromise = scope === 'commune' && scopeId
      ? (cachedContext
        ? Promise.resolve(cachedContext)
        : getCommuneMapContext(token, { scopeId }, controller.signal)
          .catch((contextError) => ({
            status: 'unavailable',
            roads: [],
            places: [],
            message: contextError?.message || 'Podkład lokalny niedostępny.',
          }))
          .then((value) => {
            contextCache.current.set(contextKey, value);
            return value;
          }))
      : Promise.resolve(null);

    // A slow commune-context service must not block the map geometry/market.
    // Context is optional and can arrive after the map is already interactive.
    if (contextKey) {
      contextPromise.then((context) => {
        if (sequence === requestSequence.current && !controller.signal.aborted) {
          setMapContext(context);
        }
      });
    }

    Promise.all([dataPromise, geometryPromise])
      .then(([mapData, geo]) => {
        if (sequence !== requestSequence.current) return;
        setData(mapData);
        setGeometry(geo);
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError' && sequence === requestSequence.current) {
          setError(requestError.message);
        }
      })
      .finally(() => {
        if (sequence === requestSequence.current) setLoading(false);
      });

    return () => controller.abort();
  }, [token, product, scope, scopeId, crmRefresh, locationRefresh]);

  useEffect(() => {
    if (!token || product !== 'kidplace' || scope !== 'commune' || !scopeId) {
      return undefined;
    }

    // Coordinate enrichment is a commune-entry concern, not a filter concern.
    // Refresh each commune at most once per mounted session. Returning through cached
    // hierarchy levels must not hit the external RSPO service again.
    const refreshKey = String(scopeId);
    if (rspoRefreshedScopes.current.has(refreshKey)) return undefined;
    rspoRefreshedScopes.current.add(refreshKey);

    const controller = new AbortController();
    refreshRspoMapLocations(token, {
      scopeId,
      product: 'kidplace',
      segments: [],
      includeMap: false,
    }, controller.signal)
      .then((refresh) => {
        if (Number(refresh?.result?.updated || 0) > 0) {
          // Coordinates only affect this commune payload. Keep parent hierarchy caches.
          dataCache.current.delete(`kidplace:commune:${scopeId}`);
          setLocationRefresh((value) => value + 1);
        }
      })
      .catch((refreshError) => {
        if (refreshError.name !== 'AbortError') {
          console.warn('RSPO commune map location refresh failed:', refreshError);
        }
      });

    return () => controller.abort();
  }, [token, product, scope, scopeId]);

  const metricOptions = useMemo(() => METRICS.filter((option) => allowedMetrics.includes(option.value)), [allowedMetrics]);
  const layerOptions = useMemo(() => (
    product === 'kidplace'
      ? [...metricOptions, { value: 'competition', label: 'Konkurencja' }, { value: 'activity', label: 'Aktywność' }]
      : metricOptions
  ), [metricOptions, product]);

  // Segment toggles are intentionally pure frontend work. Even 20 rapid clicks only
  // recompute these small arrays; no /potential request and no PHP/SQL work is started.
  const clientFilteredData = useMemo(
    () => applyKidplaceClientFilter(data, selectedSegments, onlyNewOpenings && scope === 'commune'),
    [data, selectedSegments, onlyNewOpenings, scope],
  );

  const displayData = useMemo(() => {
    if (!clientFilteredData || scope !== 'commune' || product !== 'kidplace') return clientFilteredData;

    const sourceItems = Array.from(new Map((clientFilteredData.items || []).map((item) => [String(item.id), item])).values());
    const byId = new Map(sourceItems.map((item) => [String(item.id), item]));
    const ordered = [];
    const seen = new Set();

    (clientFilteredData.groups || []).forEach((group) => {
      const rawGroupItems = (group.items || []).map((id) => byId.get(String(id))).filter(Boolean);
      const groupItems = competitionOverlay
        ? sortByCompetition(rawGroupItems, 'commune')
        : activityOverlay
          ? sortByActivity(rawGroupItems, 'commune')
          : sortByMetric(rawGroupItems, metric);
      groupItems.forEach((item) => {
        const id = String(item.id);
        if (!seen.has(id)) {
          seen.add(id);
          ordered.push(item);
        }
      });
    });

    const leftovers = sourceItems.filter((item) => !seen.has(String(item.id)));
    (competitionOverlay ? sortByCompetition(leftovers, 'commune') : activityOverlay ? sortByActivity(leftovers, 'commune') : sortByMetric(leftovers, metric))
      .forEach((item) => ordered.push(item));

    return {
      ...clientFilteredData,
      items: ordered.map((item, index) => ({ ...item, map_index: index + 1 })),
    };
  }, [clientFilteredData, scope, product, metric, competitionOverlay, activityOverlay]);

  function invalidateMarketData() {
    dataCache.current.clear();
    setCrmRefresh((value) => value + 1);
  }

  function patchParams(changes, replace = false) {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    setParams(next, { replace });
  }

  function changeProduct(nextProduct) {
    const nextMetric = defaultMetric(nextProduct, scope);
    patchParams({ product: nextProduct, metric: nextMetric, layer: nextMetric });
  }

  function changeLayer(nextLayer) {
    if ((nextLayer === 'competition' || nextLayer === 'activity') && product === 'kidplace') {
      patchParams({ layer: nextLayer });
      return;
    }
    patchParams({ layer: nextLayer, metric: nextLayer });
  }

  function navigateScope(nextScope, id) {
    const nextMetric = defaultMetric(product, nextScope);
    patchParams({
      scope: nextScope,
      scopeId: nextScope === 'country' ? null : id,
      metric: nextMetric,
      layer: competitionOverlay && product === 'kidplace' ? 'competition' : activityOverlay && product === 'kidplace' ? 'activity' : nextMetric,
    });
  }

  function selectArea(item) {
    const next = childScope(scope);
    if (next) navigateScope(next, item.id);
  }

  function handleBreadcrumb(level, id) {
    navigateScope(level, id);
  }

  function changeSegments(nextSegments) {
    const normalized = allKidplaceSegments.filter((value) => nextSegments.includes(value));
    const allSelected = normalized.length === allKidplaceSegments.length;
    patchParams({ segments: allSelected ? null : normalized.join(',') });
  }

  function handleSearch(item) {
    if (item.kind === 'facility') {
      const nextMetric = defaultMetric(product, 'commune');
      patchParams({
        scope: 'commune', scopeId: item.area_id, metric: nextMetric,
        layer: competitionOverlay && product === 'kidplace' ? 'competition' : activityOverlay && product === 'kidplace' ? 'activity' : nextMetric,
      });
      if (product === 'kidplace') {
        window.setTimeout(() => setEntityId(item.id), 0);
      }
      return;
    }
    navigateScope(item.scope, item.area_id || item.id);
  }

  return (
    <div className="prospecting-market-page">
      <header className="prospecting-market-header">
        <div className="prospecting-market-title-row">
          <div>
            <span className="prospecting-eyebrow">ONE Prospecting</span>
            <h1>Rynek</h1>
          </div>
        </div>

        <div className="prospecting-toolbar-row">
          <MarketSearch token={token} product={product} segments={product === 'kidplace' ? selectedSegments : []} onSelect={handleSearch} />
          {scope === 'country' ? (
            <DefaultButton text="Import konkurencji CSV" iconProps={{ iconName: 'Upload' }} onClick={() => setCompetitionImportOpen(true)} />
          ) : null}
          <SegmentedControl value={product} options={PRODUCTS} onChange={changeProduct} compact />
        </div>
        {product === 'kidplace' ? (
          <div className="prospecting-filter-row">
            <MarketTypeFilter selected={selectedSegments} onChange={changeSegments} />
            {scope === 'commune' ? (<label className="prospecting-opening-filter" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}><input type="checkbox" checked={onlyNewOpenings} onChange={(event) => patchParams({ openings: event.target.checked ? '1' : null })} /> Nowe wpisy i potwierdzone otwarcia</label>) : null}
          </div>
        ) : null}
      </header>

      <>
          <div className="prospecting-map-meta-row">
            <MarketBreadcrumbs breadcrumbs={displayData?.breadcrumbs || [{ level: 'country', id: 'PL', name: 'Polska' }]} onNavigate={handleBreadcrumb} />
            <SegmentedControl value={layer} options={layerOptions} onChange={changeLayer} compact />
          </div>

          {!token ? (
            <MessageBar messageBarType={MessageBarType.error}>Brak tokenu JWT użytkownika ONE w AuthContext.</MessageBar>
          ) : null}
          {error ? <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> : null}
          <div className="prospecting-market-grid">
            <section className="prospecting-map-card">
              {loading && !data ? <div className="prospecting-map-loading"><Spinner label="Wczytuję rynek…" /></div> : null}
              {displayData && geometry ? (
                <MarketMap
                  scope={scope}
                  items={scope === 'commune' && (product === 'grants' || product === 'erecruitment') ? [] : (displayData?.items || [])}
                  geometry={geometry}
                  context={mapContext}
                  metric={metric}
                  overlay={competitionOverlay ? 'competition' : activityOverlay ? 'activity' : 'metric'}
                  hoveredId={hoveredId}
                  selectedEntityId={entityId}
                  onHover={setHoveredId}
                  onSelectArea={selectArea}
                  onSelectEntity={(item) => setEntityId(item.id)}
                />
              ) : null}
              {competitionOverlay && displayData ? <CompetitionLegend items={displayData.items || []} scope={scope} /> : null}
              {activityOverlay && displayData ? <ActivityLegend items={displayData.items || []} scope={scope} /> : null}
              {loading && data ? <div className="prospecting-map-refresh">Aktualizuję…</div> : null}
              <div className="prospecting-map-caption">
                <span>Mapa Local Trends · {displayData?.scope?.name || 'Polska'}</span>
                {competitionOverlay ? (
                  <span className="is-complete">Konkurencja · lokalny snapshot ONE</span>
                ) : activityOverlay ? (
                  <span className="is-complete">Aktywność · zakończone działania z ostatnich 12 mies.</span>
                ) : (
                  <span className={displayData?.totals?.arr_complete ? 'is-complete' : 'is-incomplete'}>
                    {displayData?.totals?.arr_complete ? 'ARR kompletny' : 'ARR niepełny — pokazujemy tylko znane wartości'}
                  </span>
                )}
              </div>
            </section>

            {displayData ? (
              <MarketSideList
                token={token}
                data={displayData || data}
                metric={metric}
                overlay={competitionOverlay ? 'competition' : activityOverlay ? 'activity' : 'metric'}
                hoveredId={hoveredId}
                onHover={setHoveredId}
                onSelectArea={selectArea}
                onSelectEntity={(item) => setEntityId(item.id)}
                onSyncGroup={(group) => setCrmTarget({
                  selection_type: 'group', product: 'kidplace', scope_id: scopeId, group_name: group.name,
                  title: `Dodaj / synchronizuj: ${group.name}`,
                })}
                onSyncJst={() => setCrmTarget({
                  selection_type: 'jst', product, scope_id: scopeId,
                  title: `Dodaj / synchronizuj JST — ${displayData?.scope?.name || ''}`,
                })}
                onCrmChanged={invalidateMarketData}
              />
            ) : <aside className="prospecting-side-panel"><Spinner label="Wczytuję listę…" /></aside>}
          </div>
      </>

      <EntityPanel
        token={token}
        product={product}
        entityId={entityId}
        onOpenEntity={setEntityId}
        onDismiss={() => setEntityId(null)}
        onSyncRequest={setCrmTarget}
        onChanged={invalidateMarketData}
      />
      <CrmSyncDialog
        token={token}
        target={crmTarget}
        onDismiss={() => setCrmTarget(null)}
        onChanged={invalidateMarketData}
      />
      <CompetitionImportDialog
        token={token}
        open={competitionImportOpen}
        onDismiss={() => setCompetitionImportOpen(false)}
        onChanged={invalidateMarketData}
      />
    </div>
  );
}
